/**
 * MARAIS — marais-collection.js
 * ────────────────────────────────────────────────────────────
 * The largest client-side bundle in the theme (~22 KB). Drives
 * collection pages, the homepage product rails, and search results.
 *
 * Loaded via theme.liquid:715 on:
 *   index, collection, search, /pages/mens-homepage, /pages/womens-homepage
 *
 * Responsibilities (each section below has its own header):
 *   1. AJAX filter navigation
 *      ↳ fetchFilter() swaps #CollectionSidebar + #CollectionMain
 *        without a full page reload, then re-binds UI handlers
 *   2. Filter checkbox change handler (sizes auto-add availability=1)
 *   3. Sort select syncs URL ?sort_by= param
 *   4. Grid column toggle (1/2/3 cols, persists via localStorage)
 *   5. Mobile sidebar drawer open/close
 *   6. Price filter form submit (dollar URL params)
 *   7. Colour swatch DOM-class hex injection (CM lookup table)
 *   8. Active colour label updater (mouseenter/leave)
 *   9. Quick-add (single-variant cart/add)
 *  10. Mini cart count refresh after add
 *  11. Toast helper (shared by quick-add and quick-view)
 *  12. Quick-view modal — fetches /products/{handle}.js, renders
 *      a custom 1-2-1-2 image grid + variant picker + ATC
 *  13. Size pills on product cards (single-click add)
 *  14. Init sequence (initFiltersAndUI re-runs after every AJAX swap)
 *
 * State / persistence:
 *   localStorage 'marais-grid-cols'      — desktop grid preference
 *   localStorage 'marais-grid-cols-mob'  — mobile grid preference
 *
 * Not handled here:
 *   • Cart drawer (lives in theme.liquid inline as window.MaraisCart)
 *   • Header search (marais-search.js)
 *   • Wishlist (/pages/wishlist via wishlist-button snippet)
 */

'use strict';

(function () {

  /* ────────────────────────────────────────────────────────────
     1. AJAX FILTER NAVIGATION
     ────────────────────────────────────────────────────────────
     Instead of a full page reload, we fetch the destination URL,
     parse out just #CollectionSidebar + #CollectionMain from the
     response, and replace the equivalent nodes in the live DOM.
     Faster perceived nav + preserves scroll position.

     popstate handler ensures back/forward buttons trigger the
     same swap (without pushing another history entry).

     Catch falls back to a hard navigation if anything fails. */

  // In-memory cache of fetched section HTML, keyed by the canonical
  // (clean, no section_id) URL. Filter URLs are deterministic, so
  // toggling the same filter on/off twice hits the same key and the
  // second toggle resolves with zero network work. Per-tab only —
  // cleared on reload.
  window._mhFilterCache = window._mhFilterCache || new Map();
  var MH_FILTER_CACHE_MAX = 30;

  // Section Rendering API plumbing. We append ?section_id=<KEY> to
  // every filter fetch so Shopify returns just the section's HTML
  // (~5-20KB) instead of the entire page (~200KB+). Cuts network
  // payload and DOMParser cost dramatically on desktop, where a tap
  // would otherwise pull a fresh full page over the wire.
  //
  // KEY is derived once at load from whichever shopify-section
  // wrapper contains the AJAX targets (#CollectionSidebar or
  // #CollectionMain). For collection.json that's "main"; for any
  // template using different section keys, this picks them up
  // automatically.
  //
  // Falls back to full-HTML fetch when the section can't be
  // identified (homepage rails, search page variants), since those
  // surfaces don't follow the collection-template structure.
  var _sectionKey = (function () {
    var anchor = document.getElementById('CollectionSidebar')
              || document.getElementById('CollectionMain');
    if (!anchor) return null;
    var section = anchor.closest('[id^="shopify-section-"]');
    return section ? section.id.replace(/^shopify-section-/, '') : null;
  })();

  function _sectionFetchUrl(url) {
    if (!_sectionKey) return url;
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'section_id=' + encodeURIComponent(_sectionKey);
  }

  /* Rapid-tap filter composition.

     Shopify renders every filter value's url_to_add / url_to_remove
     against the page's initial server state. So if a user taps Filter
     A then Filter B before A's fetch lands, B's href is "current page
     + B", NOT "A + B" — and fetchFilter's AbortController kills A's
     in-flight request, so the user's A selection is lost the moment
     they tap anything else.

     Fix: compose. Diff each tapped action URL against location.href to
     extract the (single, usually) filter param being toggled, then
     apply that delta to a running _pendingComposedUrl.

     Dispatch is viewport-aware (dispatchComposed):
       • Desktop (>768px) fires immediately — zero latency penalty on
         single taps, the common case in a permanently-visible sidebar.
         Bursts still compose correctly: a mid-flight tap extends
         _pendingComposedUrl and fires again; AbortController cancels
         the first fetch and the second carries every selection.
       • Mobile (≤768px) debounces by COMPOSE_DEBOUNCE_MS. Users
         compose multiple filters inside the open drawer, so one
         trailing fetch keeps the page underneath visually stable
         instead of flashing on every tap.

     Atomic actions (pagination, Clear, popstate, sort) go through
     fetchFilter, which discards compose state so they can't be
     coalesced with prior taps. */
  var _pendingComposedUrl = null;
  var _pendingComposeTimer = null;
  var COMPOSE_DEBOUNCE_MS = 220;
  // Tracks the URL of an in-flight fetch so a tap arriving between the
  // fetch launch and its DOM swap can still seed compose state from
  // the URL we'll be at *after* the swap, not the pre-swap location.
  var _inFlightUrl = null;

  function _searchPairSet(sp) {
    var out = {};
    sp.forEach(function (v, k) { out[k + '\x00' + v] = true; });
    return out;
  }

  function composeFilterDelta(actionUrl) {
    if (!actionUrl) return;
    // pendingUrl starts as the URL we'll be at *after* the in-flight
    // fetch lands (if any), falling back to location.href. This
    // matters when a debounce fires and triggers a fetch, then the
    // user taps another filter before the fetch's DOM swap completes
    // — without this, the new compose would start from the pre-fetch
    // location.href and lose the just-fetched selection. Compose's
    // *delta* is still computed against location.href, because that
    // is the URL Shopify rendered the current data-url-add values
    // against.
    if (!_pendingComposedUrl) _pendingComposedUrl = _inFlightUrl || location.href;
    var baseSet   = _searchPairSet(new URL(location.href, location.origin).searchParams);
    var actionSet = _searchPairSet(new URL(actionUrl,     location.origin).searchParams);
    var pending   = new URL(_pendingComposedUrl, location.origin);
    // Remove pairs that exist in base but not in action — the tap unticked them.
    Object.keys(baseSet).forEach(function (kv) {
      if (actionSet[kv]) return;
      var sep = kv.indexOf('\x00');
      var k = kv.substring(0, sep);
      var v = kv.substring(sep + 1);
      var keep = pending.searchParams.getAll(k).filter(function (vv) { return vv !== v; });
      pending.searchParams.delete(k);
      keep.forEach(function (vv) { pending.searchParams.append(k, vv); });
    });
    // Add pairs that exist in action but not in base — the tap ticked them.
    Object.keys(actionSet).forEach(function (kv) {
      if (baseSet[kv]) return;
      var sep = kv.indexOf('\x00');
      var k = kv.substring(0, sep);
      var v = kv.substring(sep + 1);
      if (pending.searchParams.getAll(k).indexOf(v) === -1) {
        pending.searchParams.append(k, v);
      }
    });
    // Composing filters always lands on page 1 — preserving a deep
    // page index across a filter change usually points at a page that
    // no longer exists in the new result set.
    pending.searchParams.delete('page');
    _pendingComposedUrl = pending.toString();
  }

  // Composed entry: fires for filter taps. Preserves _pendingComposedUrl
  // so a tap arriving mid-flight composes on top of the in-flight URL.
  function fetchFilterComposed(url) {
    _fetchFilterCore(url, true, false);
  }

  // Viewport-aware dispatch. Desktop fires immediately so single taps
  // feel instant; mobile debounces so a burst inside the open drawer
  // resolves to one trailing fetch rather than several aborted ones.
  function dispatchComposed() {
    if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) {
      if (_pendingComposeTimer) clearTimeout(_pendingComposeTimer);
      _pendingComposeTimer = setTimeout(function () {
        _pendingComposeTimer = null;
        if (_pendingComposedUrl) fetchFilterComposed(_pendingComposedUrl);
      }, COMPOSE_DEBOUNCE_MS);
    } else {
      fetchFilterComposed(_pendingComposedUrl);
    }
  }

  function fetchFilter(url, pushState, scrollToTop) {
    // An explicit fetch supersedes any in-flight compose burst —
    // sort changes, pagination, Clear, popstate, etc. should not
    // be coalesced with prior filter taps (including a queued mobile
    // debounce that hasn't fired yet).
    if (_pendingComposeTimer) {
      clearTimeout(_pendingComposeTimer);
      _pendingComposeTimer = null;
    }
    _pendingComposedUrl = null;
    _fetchFilterCore(url, pushState, scrollToTop);
  }

  function _fetchFilterCore(url, pushState, scrollToTop) {
    // Bail to a hard nav if the click came from an `<a>` with no real href
    // — link.href in JS resolves "" to the current page, which would cause
    // a no-op fetch and the user would see nothing happen.
    if (!url || url === window.location.href + '#') {
      window.location.href = url || '#';
      return;
    }
    var sidebar = document.getElementById('CollectionSidebar');
    var main    = document.getElementById('CollectionMain');
    // Preserve mobile drawer open state across the swap so each
    // selection can refine options in-place (desktop-style) without
    // dismissing the drawer.
    window._mhKeepDrawerOpen = !!(sidebar && sidebar.classList.contains('collection-sidebar--open'));
    // Track in-flight URL so that taps arriving between this fetch's
    // launch and its DOM swap can compose on top of it (see
    // composeFilterDelta's _inFlightUrl read).
    _inFlightUrl = url;

    function applyHtml(html) {
      var doc        = new DOMParser().parseFromString(html, 'text/html');
      var newSidebar = doc.getElementById('CollectionSidebar');
      var newMain    = doc.getElementById('CollectionMain');
      // If neither target survived parsing (e.g. zero-result search
      // page with totally different markup), fall back to navigation.
      if (!newSidebar && !newMain) {
        window.location.href = url;
        return;
      }
      // Keep the existing <aside id="CollectionSidebar"> wrapper in
      // place and only move the new children into it. Replacing the
      // wrapper element itself briefly removes the open drawer
      // (translateY(0)) and inserts a fresh wrapper that hits the
      // default translateY(100%) base style — visually that's the
      // drawer sliding off-screen on every filter tap. Keeping the
      // wrapper preserves the open class, scroll position, and
      // overlay/body-lock state.
      //
      // ALSO preserve user-visible state across the swap:
      //   - which <details> accordions are open (category tree)
      //   - the sidebar's vertical scroll position
      //   - which element had keyboard focus
      // Without this, every filter tap visually re-renders the
      // entire menu — accordions snap closed, scroll jumps to
      // top, and focus is lost. Feels like a reload to the user
      // even though it's the legitimate AJAX swap landing.
      if (newSidebar && sidebar) {
        // 1. Snapshot the state we want to keep, BEFORE swap.
        var openDetailsIds = [];
        sidebar.querySelectorAll('details[open]').forEach(function (d) {
          // Use a stable identifier — prefer id, fall back to a
          // dot-path of position among same-tag siblings so the
          // new DOM's matching node can be found even when ids
          // aren't set on every <details>.
          if (d.id) {
            openDetailsIds.push('#' + d.id);
          } else {
            // Build a positional selector: tag:nth-of-type(N) chain
            var path = [];
            var node = d;
            while (node && node !== sidebar) {
              var siblings = node.parentNode ? node.parentNode.children : [];
              var idx = 1;
              for (var i = 0; i < siblings.length; i++) {
                if (siblings[i] === node) break;
                if (siblings[i].tagName === node.tagName) idx += 1;
              }
              path.unshift(node.tagName.toLowerCase() + ':nth-of-type(' + idx + ')');
              node = node.parentNode;
            }
            openDetailsIds.push(path.join(' > '));
          }
        });
        var savedScroll = sidebar.scrollTop;
        var savedFocusSelector = null;
        var activeEl = document.activeElement;
        if (activeEl && sidebar.contains(activeEl)) {
          // Best-effort focus restoration via id or data-url-add
          // (filter options carry these).
          if (activeEl.id) {
            savedFocusSelector = '#' + activeEl.id;
          } else if (activeEl.dataset && activeEl.dataset.urlAdd) {
            savedFocusSelector = '[data-url-add="' + activeEl.dataset.urlAdd + '"]';
          }
        }

        // 2. Atomic swap — replaceChildren applies all child
        //    changes in a single layout pass, so the drawer never
        //    spends a frame as an empty container.
        var newKids = Array.from(newSidebar.children);
        sidebar.replaceChildren.apply(sidebar, newKids);

        // 3. Restore the snapshotted state on the new DOM.
        openDetailsIds.forEach(function (sel) {
          var node = sidebar.querySelector(sel);
          if (node && node.tagName === 'DETAILS') node.open = true;
        });
        sidebar.scrollTop = savedScroll;
        if (savedFocusSelector) {
          var newFocus = sidebar.querySelector(savedFocusSelector);
          if (newFocus && typeof newFocus.focus === 'function') {
            newFocus.focus({ preventScroll: true });
          }
        }
      }
      if (newMain && main) main.replaceWith(newMain);
      if (pushState !== false) history.pushState(null, '', url);
      // Swap landed — location.href now matches the URL we were
      // composing on top of. Clear both in-flight and compose state so
      // subsequent taps reseed from the new location. If a newer tap
      // has already extended _pendingComposedUrl past this URL, leave
      // it — that newer fetch will be authoritative when it lands.
      if (_inFlightUrl === url) _inFlightUrl = null;
      if (_pendingComposedUrl === url) _pendingComposedUrl = null;
      initFiltersAndUI();
      // Pagination clicks (and only those) ask for the viewport to
      // reset to the top of the page so the next page of products
      // doesn't appear far below the user's current scroll position.
      // Filter / category taps deliberately preserve scroll.
      if (scrollToTop) {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    }

    // Cache hit — no network, no parse-delay. Drawer stays open
    // continuously because the swap is synchronous.
    var canonical = new URL(url, location.origin).href;
    if (window._mhFilterCache.has(canonical)) {
      applyHtml(window._mhFilterCache.get(canonical));
      return;
    }

    // Abort any in-flight refresh — rapid filter tapping otherwise queues
    // sequential round-trips and the user pays full latency for each.
    if (window._mhFilterCtrl) window._mhFilterCtrl.abort();
    var ctrl = new AbortController();
    window._mhFilterCtrl = ctrl;

    // Fetch only the section's HTML, not the full page. Cache key
    // stays the canonical (clean) URL so cache hits work on the
    // user-facing URL, not the section-id-decorated one.
    fetch(_sectionFetchUrl(url), { signal: ctrl.signal })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        window._mhFilterCache.set(canonical, html);
        // FIFO eviction — Map preserves insertion order.
        if (window._mhFilterCache.size > MH_FILTER_CACHE_MAX) {
          var firstKey = window._mhFilterCache.keys().next().value;
          window._mhFilterCache.delete(firstKey);
        }
        applyHtml(html);
      })
      .catch(function (err) {
        // Intentional aborts (newer fetch superseded this one) are
        // expected and should be silent. Only fall back on real errors.
        if (err && err.name === 'AbortError') return;
        window.location.href = url;
      });
  }

  function navigateTo(url) { fetchFilter(url); }

  window.addEventListener('popstate', function () {
    fetchFilter(location.href, false);
  });

  /* Optimistic chip rendering. When a user ticks/unticks a filter
     checkbox we paint the chip change immediately so they don't wait
     for the fetch round-trip to see visible feedback. The server
     response then renders the canonical chip set authoritatively. */
  function addChipToRail(text, removeUrl) {
    if (!text) return;
    var sidebar = document.getElementById('CollectionSidebar');
    if (!sidebar) return;
    var rail = sidebar.querySelector('.active-chips');
    if (!rail) {
      // First filter applied — server didn't render the rail at all.
      // Recreate the wrapper with BOTH row buckets so subsequent
      // chips can slot into the right one without DOM gymnastics.
      rail = document.createElement('div');
      rail.className = 'active-chips';
      rail.setAttribute('role', 'list');
      rail.setAttribute('aria-label', 'Active filters');
      var rowOthers = document.createElement('div');
      rowOthers.className = 'active-chips__row active-chips__row--others';
      var rowDesigners = document.createElement('div');
      rowDesigners.className = 'active-chips__row active-chips__row--designers';
      rail.appendChild(rowOthers);
      rail.appendChild(rowDesigners);
      var topbar = sidebar.querySelector('.sidebar__topbar');
      if (topbar && topbar.parentNode) topbar.parentNode.insertBefore(rail, topbar.nextSibling);
      else sidebar.appendChild(rail);
    }
    // Dedupe — if a chip with the same removeUrl is already in the rail
    // (server-rendered or optimistically added), don't append a second
    // copy. Rapid taps on the same filter <a> would otherwise paint
    // duplicate chips for one logical selection. Skip chips that have
    // been optimistically hidden (visibility:hidden) — those are en
    // route to removal and the user is re-adding the filter.
    var hrefMatch = removeUrl || '#';
    var existing = rail.querySelectorAll('.active-chip');
    for (var ei = 0; ei < existing.length; ei++) {
      if (existing[ei].getAttribute('href') === hrefMatch &&
          existing[ei].style.visibility !== 'hidden') {
        return;
      }
    }
    // Designer flag is passed in by the caller (third arg). Caller
    // knows what was actually tapped — using the URL to guess would
    // misfire when the URL carries pre-existing state from other
    // filters (e.g. tapping a category while a vendor is already
    // selected → url_to_remove contains both filter.p.vendor and
    // filter.p.product_type, so URL-based detection would wrongly
    // bucket the new chip as a designer).
    var isDesigner = arguments[2] === true;
    var chip = document.createElement('a');
    chip.href = hrefMatch;
    chip.className = 'active-chip' + (isDesigner ? ' active-chip--designer' : '');
    chip.setAttribute('role', 'listitem');
    chip.textContent = text;
    var x = document.createElement('span');
    x.className = 'active-chip__x';
    x.setAttribute('aria-hidden', 'true');
    x.textContent = '×';
    chip.appendChild(x);
    // Drop into the right bucket. Fall back to appending to the rail
    // (search bar style — .active-chips--bar — has no row buckets).
    var bucketSel = isDesigner ? '.active-chips__row--designers'
                               : '.active-chips__row--others';
    var bucket = rail.querySelector(bucketSel);
    (bucket || rail).appendChild(chip);

    // The sidebar rail is hidden on desktop (≥861px) — without also
    // updating the desktop bar rail (.active-chips--bar) and the
    // mobile sticky strip (.active-chips--mobile), desktop users get
    // no visible feedback on tap. Mirror the chip into each flat rail
    // that exists; the rails are conditional in Liquid, so create
    // .active-chips--bar on the fly when this is the first filter.
    syncFlatChipRails(text, hrefMatch);
  }

  function makeFlatChip(text, removeUrl) {
    var chip = document.createElement('a');
    chip.href = removeUrl || '#';
    chip.className = 'active-chip';
    chip.setAttribute('role', 'listitem');
    chip.textContent = text;
    var x = document.createElement('span');
    x.className = 'active-chip__x';
    x.setAttribute('aria-hidden', 'true');
    x.textContent = '×';
    chip.appendChild(x);
    return chip;
  }

  function appendChipDedup(rail, text, removeUrl) {
    if (!rail) return;
    var existing = rail.querySelectorAll('.active-chip');
    for (var i = 0; i < existing.length; i++) {
      if (existing[i].getAttribute('href') === removeUrl &&
          existing[i].style.visibility !== 'hidden') return;
    }
    rail.appendChild(makeFlatChip(text, removeUrl));
  }

  function syncFlatChipRails(text, removeUrl) {
    // Desktop bar rail — create if the page rendered with no chips.
    var bar = document.querySelector('.active-chips--bar');
    if (!bar) {
      var barLeft = document.querySelector('.collection-bar__left');
      if (barLeft) {
        bar = document.createElement('div');
        bar.className = 'active-chips active-chips--bar';
        bar.setAttribute('role', 'list');
        bar.setAttribute('aria-label', 'Active filters');
        barLeft.appendChild(bar);
      }
    }
    appendChipDedup(bar, text, removeUrl);
    // Mobile sticky strip — only update if it already exists. Creating
    // it from scratch needs a specific insertion point and is rare
    // (mobile users see the sidebar rail in the open drawer anyway).
    appendChipDedup(document.querySelector('.active-chips--mobile'), text, removeUrl);
  }
  function hideChipByUrl(removeUrl) {
    if (!removeUrl) return;
    // visibility:hidden keeps the chip's bounding box in flow so the
    // chip rail height + row max-content stay stable through the
    // compose debounce + fetch window. The AJAX swap replaces the
    // entire rail with the canonical server-rendered version.
    document.querySelectorAll('.active-chip').forEach(function (chip) {
      if (chip.getAttribute('href') === removeUrl) chip.style.visibility = 'hidden';
    });
  }
  function chipLabelFromCheckbox(cb) {
    var label = cb.closest('label');
    if (!label) return '';
    var span = label.querySelector('.filter-opt__label, .filter-size__label, .filter-colour__label');
    return span ? span.textContent.trim() : label.textContent.trim();
  }
  function chipLabelFromFcatOpt(link) {
    var span = link.querySelector('.fcat-opt__lbl');
    if (span) return span.textContent.trim();
    // Fallback: strip the count suffix
    return link.textContent.replace(/\(\d+\)\s*$/, '').trim();
  }
  function addOptimisticChip(cb) {
    // Brand/designer filter checkboxes live in a .filter-group whose
    // heading text identifies the filter type. Match on the same
    // tokens the server uses (vendor / brand / designer) so the
    // bucket placement matches server output exactly.
    var group = cb.closest('.filter-group');
    var head = group ? group.querySelector('.filter-group__head, .filter-group__title') : null;
    var headText = head ? (head.textContent || '').toLowerCase() : '';
    var isDesigner = headText.indexOf('vendor')   !== -1
                  || headText.indexOf('brand')    !== -1
                  || headText.indexOf('designer') !== -1;
    addChipToRail(chipLabelFromCheckbox(cb), cb.dataset.urlRemove, isDesigner);
  }
  function hideOptimisticChip(cb) {
    hideChipByUrl(cb.dataset && cb.dataset.urlRemove);
  }

  /* Sync the "Clear (N)" button with the live chip count. Called from
     initFiltersAndUI after each AJAX swap so the count stays accurate. */
  function updateClearButton() {
    var btn = document.getElementById('SidebarClearBtn');
    if (!btn) return;
    // The same chip list renders in both the sidebar rail and the mobile
    // strip in #CollectionMain, so a global selector double-counts. Scope
    // to the sidebar rail only.
    var sidebar = document.getElementById('CollectionSidebar');
    var count = sidebar ? sidebar.querySelectorAll('.active-chip').length : 0;
    var countEl = document.getElementById('SidebarClearCount');
    if (countEl) countEl.textContent = count;
    btn.hidden = count === 0;
  }

  /* Intercept <a> filter links — category tree, active chips, pagination,
     and the sidebar "Clear (n)" reset link. Without this delegation the
     Clear link does a native browser nav, which would close the mobile
     drawer; routing it through fetchFilter() preserves the open state
     via window._mhKeepDrawerOpen so the customer can keep refining
     without re-opening the modal. */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.fcat-opt, .active-chip, .pagination a, .sidebar__clear');
    if (!link) return;
    // Bail to native if the link has no real href — prevents the click
    // from being swallowed when Shopify's filter API returns no URL.
    var rawHref = link.getAttribute('href');
    if (!rawHref || rawHref === '#') return;
    e.preventDefault();

    // Optimistic UI for chip removals and category-tree taps so the
    // user sees the visual change immediately, not after the fetch.
    if (link.classList.contains('active-chip')) {
      // Hide the chip but keep its bounding box in flow — using
      // `visibility: hidden` (not `display: none`) means the row's
      // max-content width is unchanged, the drawer's overflow-x
      // scroll position doesn't reshuffle, and the two-row layout
      // doesn't reflow. The AJAX swap that lands ~250-500ms later
      // replaces the rail entirely so the chip is properly gone
      // after that — the in-between frame just shows blank space.
      link.style.visibility = 'hidden';
      var chipHref = link.getAttribute('href');
      if (chipHref) {
        document.querySelectorAll('.fcat-opt').forEach(function (o) {
          if (o.dataset && o.dataset.urlRemove === chipHref) {
            o.classList.remove('fcat-opt--on', 'is-active');
          }
        });
      }
    } else if (link.classList.contains('fcat-opt')) {
      if (link.classList.contains('fcat-opt--on')) {
        hideChipByUrl(link.dataset && link.dataset.urlRemove);
        // Drop the highlight so the tap reads as an untick straight away.
        link.classList.remove('fcat-opt--on', 'is-active');
      } else {
        // The generic-fallback renderer (vendor/brand/materials)
        // emits .fcat-opt--leaf elements just like the category
        // tree does — both share the same handler. Detect designer
        // intent the same way as filter checkboxes: check the
        // enclosing .filter-group's heading text for the 'vendor'/
        // 'brand'/'designer' tokens the server uses to bucket
        // chips_designers_inner. The category tree's filter-group
        // heading is "Categories" — never matches — so chip
        // correctly lands in --others.
        var _fopGroup = link.closest('.filter-group');
        var _fopHead = _fopGroup ? _fopGroup.querySelector('.filter-group__head, .filter-group__title') : null;
        var _fopHeadText = _fopHead ? (_fopHead.textContent || '').toLowerCase() : '';
        var _fopIsDesigner = _fopHeadText.indexOf('vendor')   !== -1
                          || _fopHeadText.indexOf('brand')    !== -1
                          || _fopHeadText.indexOf('designer') !== -1;
        addChipToRail(chipLabelFromFcatOpt(link), link.dataset && link.dataset.urlRemove, _fopIsDesigner);
        // Optimistic highlight — paint the leaf as selected the moment
        // the tap registers so the user gets feedback during the
        // ~220ms compose debounce + fetch round-trip. The server-
        // rendered DOM lands with the same classes after applyHtml.
        link.classList.add('fcat-opt--on', 'is-active');
      }
    } else if (link.classList.contains('sidebar__clear')) {
      // Optimistic paint: hide every chip and un-tick every category leaf
      // so the drawer visually empties the moment the user taps Clear,
      // not after the AJAX round-trip lands. Use visibility:hidden so
      // the rail's reserved height (74px) stays steady through the
      // debounce + fetch — display:none would have collapsed each row
      // and the filter groups below would have shifted up briefly.
      document.querySelectorAll('.active-chip').forEach(function (c) { c.style.visibility = 'hidden'; });
      document.querySelectorAll('.fcat-opt--on').forEach(function (o) {
        o.classList.remove('fcat-opt--on', 'is-active');
      });
      document.querySelectorAll('.filter-opt--active').forEach(function (l) {
        l.classList.remove('filter-opt--active');
      });
    }

    // Pagination links scroll to the top of the page after the swap so
    // the next page of products lands in view, not below the fold.
    var isPagination = !!link.closest('.pagination');

    // Pagination + Clear are atomic — fire immediately, bypass compose.
    // Filter taps (.fcat-opt) and chip removals route through compose so
    // a fast burst of taps survives as a single fetch carrying every
    // selection, not just the last one.
    if (isPagination || link.classList.contains('sidebar__clear')) {
      var targetUrl = link.href;
      if (isPagination) {
        // Merge query params the current URL carries but the pagination
        // href lacks (utm_*, plus a guard against server-rendered links
        // missing filter params). Per-key "target wins": if the link
        // already names a key, its values are authoritative — correct
        // for multi-value filter params, which Liquid emits in full.
        var target = new URL(link.href, location.origin);
        var current = new URL(location.href).searchParams;
        var copied = {};
        current.forEach(function (_, k) {
          if (k === 'page' || copied[k] || target.searchParams.has(k)) return;
          copied[k] = true;
          current.getAll(k).forEach(function (v) {
            target.searchParams.append(k, v);
          });
        });
        targetUrl = target.toString();
      }
      fetchFilter(targetUrl, undefined, isPagination);
    } else {
      composeFilterDelta(link.href);
      dispatchComposed();
    }
  });

  /* Click on an L3 category summary (e.g. "Jackets", "Jewelry") that
     wraps L4 leaves: on DESKTOP, apply every leaf as a product_type
     filter so the grid narrows to "all Jackets". No chip is rendered
     — the user is browsing the category, not building a filter set.
     We don't preventDefault, so the native <summary> click still
     toggles the <details> open. Skip if any leaf is already active
     — in that state the user is mid-refining and just wants to
     collapse the section, not re-fetch the same URL.

     On MOBILE (≤860px), the summary is purely an accordion toggle
     — customers want to expand and pick specific leaves rather than
     load the concatenated "all children" result. Bail before the
     compose / fetch; the native <details>/<summary> behaviour still
     opens the section. */
  document.addEventListener('click', function (e) {
    var summary = e.target.closest('.fcat-l3__sum[data-l3-url]');
    if (!summary) return;
    if (e.target.closest('.fcat-opt')) return;
    if (window.innerWidth <= 860) return;
    var details = summary.parentElement;
    if (details && details.querySelector('.fcat-opt--on')) return;
    var url = summary.getAttribute('data-l3-url');
    if (!url) return;
    composeFilterDelta(url);
    dispatchComposed();
  });

  /* ────────────────────────────────────────────────────────────
     2. FILTER CONTROLS
     ──────────────────────────────────────────────────────────── */

  /* Hidden checkbox (sr-only) reads data-url-add / data-url-remove
     baked in by Shopify's filter API. We just navigate to it.
     Special case: when ticking a SIZE filter, we also force
     filter.v.availability=1 so the user only sees variants
     actually in stock for that size — the size-pill UX implies
     "show me what I can buy in this size", not "show me all
     products that ever shipped this size". */
  function initFilterCheckboxes() {
    document.querySelectorAll('.filter-checkbox').forEach(function (cb) {
      cb.addEventListener('change', function () {
        var url = this.checked ? this.dataset.urlAdd : this.dataset.urlRemove;
        if (!url) return;
        if (this.checked && this.closest('.filter-size-grid')) {
          if (url.indexOf('filter.v.availability') === -1) {
            url = url + (url.indexOf('?') === -1 ? '?' : '&') + 'filter.v.availability=1';
          }
        }
        if (this.checked) addOptimisticChip(this);
        else              hideOptimisticChip(this);
        // Optimistic highlight on the parent label so size pills /
        // colour swatches / generic options visibly toggle the
        // moment the tap registers, not after the AJAX swap.
        var lbl = this.closest('label');
        if (lbl) lbl.classList.toggle('filter-opt--active', this.checked);
        composeFilterDelta(url);
        dispatchComposed();
      });
    });
  }

  function initSortSelect() {
    var select = document.getElementById('SortBy');
    if (!select) return;
    select.addEventListener('change', function () {
      var url = new URL(window.location.href);
      // Reset to page 1 when sort changes — avoids landing on a page
      // that doesn't exist under the new sort order.
      url.searchParams.delete('page');
      if (this.value === 'newest' || this.value === '') {
        // "Newest" is our metafield order — no ?sort_by param needed.
        url.searchParams.delete('sort_by');
      } else {
        url.searchParams.set('sort_by', this.value);
      }
      navigateTo(url.toString());
    });
    // Sync select to current URL state. The "newest" option is already
    // marked selected in the Liquid HTML when sort_by is absent, so we
    // only need to handle the case where a non-newest sort_by is present.
    var current = new URLSearchParams(window.location.search).get('sort_by');
    if (current && current !== 'newest') select.value = current;
  }

  /* Column toggle: 1/2/3 desktop, 1/2 mobile. Persists per device
     in separate localStorage keys so a phone user's 2-col preference
     doesn't override their 3-col on desktop. */
  function initGridToggle() {
    var grid    = document.getElementById('ProductGrid');
    if (!grid) return;
    var isMobile = window.innerWidth <= 768;
    var storageKey = isMobile ? 'marais-grid-cols-mob' : 'marais-grid-cols';
    // <html> data-attribute that's authoritative for column count
    // — the head-time hydrator (layout/theme.liquid) sets it pre-
    // paint to avoid a 3→4 flash; this toggle must keep it in sync
    // when the customer clicks, or the attribute selectors in the
    // CSS (specificity 0,2,0) outweigh the class swap (0,1,0) and
    // the live swap silently no-ops.
    var attrKey = isMobile ? 'data-grid-cols-mobile' : 'data-grid-cols-desktop';
    var defaultCols = isMobile ? '2' : ((grid.className.match(/product-grid--(\d)-col/) || [])[1] || '3');
    var buttons = document.querySelectorAll(isMobile ? '.grid-toggle--mobile .grid-toggle__btn' : '.grid-toggle--desktop .grid-toggle__btn');
    if (!buttons.length) return;
    var saved = localStorage.getItem(storageKey) || defaultCols;
    applyGrid(saved);
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyGrid(this.dataset.cols);
        localStorage.setItem(storageKey, this.dataset.cols);
      });
    });
    function applyGrid(cols) {
      grid.className = grid.className.replace(/product-grid--\d-col/g, '').trim();
      grid.classList.add('product-grid--' + cols + '-col');
      document.documentElement.setAttribute(attrKey, cols);
      buttons.forEach(function (b) {
        b.classList.toggle('grid-toggle__btn--active', b.dataset.cols === cols);
        b.setAttribute('aria-pressed', b.dataset.cols === cols ? 'true' : 'false');
      });
    }
  }

  /* Mobile filter drawer (full-screen takeover on phones).
     Locks body scroll while open. ESC closes. */
  function initMobileSidebar() {
    var openBtn  = document.getElementById('MobFilterBtn');
    var closeBtn = document.getElementById('SidebarCloseBtn');
    var sidebar  = document.getElementById('CollectionSidebar');
    var overlay  = document.getElementById('SidebarOverlay');
    var applyBtn = document.getElementById('SidebarApplyBtn');
    if (!sidebar) return;
    function resetApply() {
      if (applyBtn) { applyBtn.hidden = true; applyBtn.classList.remove('is-on'); }
    }
    function open() {
      sidebar.classList.add('collection-sidebar--open');
      if (overlay) { overlay.classList.add('sidebar-overlay--open'); overlay.setAttribute('aria-hidden', 'false'); }
      document.body.style.overflow = 'hidden';
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      // View Results button always visible while the drawer is open.
      if (applyBtn) { applyBtn.hidden = false; applyBtn.classList.add('is-on'); }
    }
    function close() {
      sidebar.classList.remove('collection-sidebar--open');
      if (overlay) { overlay.classList.remove('sidebar-overlay--open'); overlay.setAttribute('aria-hidden', 'true'); }
      document.body.style.overflow = '';
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      resetApply();
    }
    if (openBtn)  openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay)  overlay.addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });

    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        // Filters now refine the sidebar live as the user taps, so the
        // apply button just closes the drawer to reveal the results.
        close();
      });
    }

    // After an AJAX swap triggered while the drawer was open, re-open
    // the fresh sidebar so the user keeps filtering uninterrupted.
    if (window._mhKeepDrawerOpen) {
      open();
      window._mhKeepDrawerOpen = false;
    }
  }

  /* Price form: dollars in UI → dollars in URL (Shopify reads
     filter.v.price.* params in currency units, not cents).
     Resetting `page` so price filter doesn't land on page 4 of an
     old filter set with no results. */
  function initPriceForm() {
    var form = document.getElementById('PriceFilterForm');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var url    = new URL(window.location.href);
      var params = url.searchParams;
      params.delete('filter.v.price.gte');
      params.delete('filter.v.price.lte');
      var min = document.getElementById('FilterMinPrice');
      var max = document.getElementById('FilterMaxPrice');
      // Shopify reads filter.v.price.* URL params in DOLLARS, not cents
      // (verified live: gte=500 matches $500+, gte=50000 matches
      // $50,000+). The old ×100 conversion made every price filter
      // 100× too high and returned zero products.
      if (min && min.value) params.set('filter.v.price.gte', parseFloat(min.value).toFixed(2));
      if (max && max.value) params.set('filter.v.price.lte', parseFloat(max.value).toFixed(2));
      params.delete('page');
      navigateTo(url.toString());
    });
  }

  /* Colour swatch lookup table.
     Maps colloquial colour names → hex. Multiple synonyms per
     hex is intentional (e.g. "noir" / "jet" / "black" all → #111).
     Hyphenated keys: "off-white" rather than "off white" because
     product-card.liquid does cv | downcase then JS replaces spaces
     with hyphens before lookup.
     Audit note: there's a smaller COLOUR_MAP in marais-product.liquid
     with different values for the same names — flagged for consolidation. */
  function initColourSwatches() {
    var CM = {
      /* Blacks */
      'black':'#111','noir':'#111','jet':'#111','off-black':'#222',
      /* Whites */
      'white':'#f5f5f5','bright-white':'#f5f5f5','optic-white':'#f5f5f5',
      /* Creams / ivories */
      'ivory':'#fffff0','cream':'#fffdd0','ecru':'#fffdd0','off-white':'#f8f4ef','bone':'#f8f4ef','parchment':'#f8f4ef',
      /* Neutrals */
      'beige':'#f5f0e8','nude':'#f5f0e8','natural':'#f5f0e8','linen':'#f5f0e8',
      'sand':'#c2b280','stone':'#c2b280','oat':'#c2b280','wheat':'#c2b280',
      'camel':'#c19a6b','caramel':'#c19a6b','honey':'#c19a6b',
      'tan':'#d2b48c','toffee':'#d2b48c',
      'taupe':'#b09f8c','mushroom':'#b09f8c','mink':'#b09f8c',
      /* Browns */
      'brown':'#8b4513','chocolate':'#7b3f00','cognac':'#7b3f00','tobacco':'#7b3f00',
      /* Greys */
      'grey':'#878787','gray':'#878787','slate':'#878787',
      'light-grey':'#cccccc','light-gray':'#cccccc','pale-grey':'#cccccc','silver-grey':'#cccccc',
      'silver':'#c0c0c0',
      'charcoal':'#36454f','dark-grey':'#36454f','dark-gray':'#36454f','graphite':'#36454f','anthracite':'#36454f',
      /* Reds */
      'red':'#cc0000','tomato':'#cc0000','scarlet':'#cc0000','crimson':'#cc0000',
      'burgundy':'#800020','wine':'#800020','bordeaux':'#800020','merlot':'#800020','claret':'#800020','oxblood':'#800020',
      /* Pinks */
      'pink':'#ffc0cb','blush':'#ffb6c1','petal':'#ffb6c1','powder-pink':'#ffb6c1','baby-pink':'#ffb6c1','peach':'#ffb6c1',
      'rose':'#e75480','dusty-rose':'#e75480','antique-rose':'#e75480',
      'coral':'#ff7f50','terracotta':'#ff7f50',
      /* Oranges / Yellows */
      'orange':'#ff6600','amber':'#ff6600','rust':'#b7410e','burnt-orange':'#b7410e',
      'yellow':'#ffd700','butter':'#ffd700',
      'mustard':'#ffdb58','ochre':'#ffdb58','saffron':'#ffdb58',
      'olive':'#808000','khaki':'#808000',
      /* Greens */
      'green':'#3a7d44','bottle-green':'#3a7d44','hunter-green':'#3a7d44',
      'sage':'#9caf88','pistachio':'#9caf88','celadon':'#9caf88',
      'emerald':'#006400','forest':'#228b22','racing-green':'#228b22',
      'mint':'#98ff98','aqua':'#00ffff','teal':'#008080','jade':'#008080','duck-egg':'#008080',
      /* Blues */
      'blue':'#1e3a8a','royal-blue':'#1e3a8a','electric-blue':'#1e3a8a',
      'navy':'#001f5b','navy-blue':'#001f5b','dark-navy':'#001f5b','midnight':'#001f5b','midnight-blue':'#001f5b','marine':'#001f5b',
      'cobalt':'#0047ab','indigo':'#0047ab',
      'denim':'#1560bd','chambray':'#1560bd',
      'sky':'#87ceeb','powder-blue':'#87ceeb','baby-blue':'#87ceeb','cornflower':'#87ceeb',
      /* Purples */
      'purple':'#6b21a8','violet':'#6b21a8','plum':'#6b21a8','aubergine':'#6b21a8','eggplant':'#6b21a8',
      'lilac':'#c084fc','lavender':'#c084fc','mauve':'#c084fc',
      /* Metallics */
      'metallic':'#a8a9ad','pewter':'#a8a9ad','bronze':'#a8a9ad',
      'gold':'#d4a017','champagne':'#d4a017',
      /* Multi / print */
      'multi':'#b86bdf','multicolour':'#b86bdf','multicolor':'#b86bdf','print':'#b86bdf','pattern':'#b86bdf'
    };
    document.querySelectorAll('.product-card__swatch[data-c]').forEach(function (el) {
      var key = el.getAttribute('data-c').replace(/ /g, '-');
      if (CM[key]) el.style.background = CM[key];
    });
  }

  /* Hover label for colour filter swatches.
     On mouseenter: shows the hovered colour name beside the filter heading.
     On mouseleave: reverts to the comma-joined list of currently-active
     colours, or empty if none.
     `title.split(' (')[0]` strips the "(12)" count suffix. */
  function initColourLabel() {
    var label = document.getElementById('ColourFilterLabel');
    if (!label) return;
    document.querySelectorAll('.filter-colour').forEach(function (el) {
      el.addEventListener('mouseenter', function () {
        var t = this.getAttribute('title');
        if (t) label.textContent = t.split(' (')[0];
      });
      el.addEventListener('mouseleave', function () {
        var active = [];
        document.querySelectorAll('.filter-colour--active').forEach(function (a) {
          active.push(a.getAttribute('title').split(' (')[0]);
        });
        label.textContent = active.join(', ');
      });
    });
  }

  /* ────────────────────────────────────────────────────────────
     PRODUCT CARD INTERACTIONS
     ──────────────────────────────────────────────────────────── */

  /* Quick-add for single-variant products (rendered by product-card
     when product.variants.size == 1). One-click POST to /cart/add.js,
     toast on success, refresh cart drawer if MaraisCart exposed. */
  function initQuickAdd() {
    document.querySelectorAll('.product-card__quick-add[data-variant-id]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var variantId = this.dataset.variantId;
        var orig = this.textContent;
        this.textContent = 'Adding…';
        this.style.opacity = '.7';
        this.style.pointerEvents = 'none';
        var self = this;
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (d.id) { showToast(toastString('Added', 'Added to bag')); if (window.MaraisCart) { window.MaraisCart.refresh(true); } else { updateCartCount(); } }
          else showToast(d.message || 'Could not add to bag');
        })
        .catch(function () { showToast(toastString('Error', 'Could not add to bag')); })
        .finally(function () { self.textContent = orig; self.style.opacity = ''; self.style.pointerEvents = ''; });
      });
    });
  }

  /* Fallback cart-count refresher used only when the mini-cart
     drawer (window.MaraisCart) isn't on the page — e.g. during
     a Shopify-section-rendering preview. Normally the cart drawer
     handles badge updates centrally. */
  function updateCartCount() {
    fetch(window.Shopify.routes.root + 'cart.js').then(function (r) { return r.json(); }).then(function (c) {
      var badge = document.querySelector('.mh-cart-badge');
      if (badge) { badge.textContent = c.item_count; }
      else if (c.item_count > 0) {
        var cartBtn = document.querySelector('.mh-cart-btn');
        if (cartBtn) {
          var b = document.createElement('span');
          b.className = 'mh-cart-badge';
          b.textContent = c.item_count;
          cartBtn.appendChild(b);
        }
      }
    });
  }

  /* Single-instance toast timer — module-scoped so a quick second
     toast resets the timer instead of stacking. 2.4s show duration.
     Toast element is rendered by theme.liquid (#marais-toast).
     Translation strings are read from data-i18n-* attributes set
     server-side so we don't ship English strings in JS. */
  var toastTimer;
  function showToast(msg) {
    var t = document.getElementById('marais-toast');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('marais-toast--show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove('marais-toast--show'); }, 2400);
  }
  /* Toast variant that embeds a single anchor in the body — used
     when "wishlist"/"cart"/etc. should be tappable and route to
     its page. Built via DOM methods (createTextNode + appendChild)
     so no innerHTML path: callers pass plain strings and an href,
     never an HTML fragment, so there's no XSS surface even when
     the prefix/suffix contain values derived from data attributes. */
  function showToastWithLink(prefix, linkText, linkHref, suffix) {
    var t = document.getElementById('marais-toast');
    if (!t) return;
    while (t.firstChild) t.removeChild(t.firstChild);
    if (prefix) t.appendChild(document.createTextNode(prefix));
    var a = document.createElement('a');
    a.href = linkHref;
    a.textContent = linkText;
    t.appendChild(a);
    if (suffix) t.appendChild(document.createTextNode(suffix));
    t.classList.add('marais-toast--show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove('marais-toast--show'); }, 2400);
  }
  function toastString(key, fallback) {
    var t = document.getElementById('marais-toast');
    return (t && t.dataset['i18n' + key]) || fallback;
  }


  /* Wishlist hearts on product cards. Click the heart → if the
     product has multiple sizes, a tiny dropdown opens listing them;
     picking a size saves the (product, variant, size) to localStorage
     under the same 'marais-wishlist' key the wishlist-button snippet
     uses. Single-variant products skip the dropdown. Event-delegated
     so it survives AJAX filter swaps without rebinding. */
  function readWishlist() {
    try {
      var raw = localStorage.getItem('marais-wishlist');
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }
  function writeWishlist(items) {
    try { localStorage.setItem('marais-wishlist', JSON.stringify(items)); } catch (e) {}
    try { window.dispatchEvent(new CustomEvent('marais:wishlist-change', { detail: { items: items } })); } catch (e) {}
  }
  /* Fire-and-forget sync to the MARAIS Wishlist worker. Matches the
     endpoint + field shape used by snippets/wishlist-button.liquid and
     layout/theme.liquid's cart-drawer Move-to-wishlist. Silent on
     failure — localStorage is the source of truth.
     Endpoint sourced from window.MARAIS_WISHLIST_ENDPOINT (set by Liquid
     from settings.wishlist_worker_url) so all three storefront callers
     share one source of truth. Hardcoded fallback covers the case where
     the inline-Liquid <script> didn't run yet. */
  var WL_WORKER_ENDPOINT =
    (typeof window !== 'undefined' && window.MARAIS_WISHLIST_ENDPOINT) ||
    'https://marais-wishlist.de-po.workers.dev/storefront/wishlist';
  function postToWorker(item) {
    if (!item || !item.productId) return;
    var body = new URLSearchParams();
    body.set('productId', item.productId);
    body.set('title', item.title || '');
    if (item.handle)    body.set('handle', item.handle);
    if (item.vendor)    body.set('vendor', item.vendor);
    if (item.url)       body.set('url', item.url);
    if (item.imageUrl)  body.set('imageUrl', item.imageUrl);
    if (item.price)     body.set('price', item.price);
    if (item.variantId) body.set('variantId', item.variantId);
    if (item.size)      body.set('size', item.size);
    // Forward logged-in customer.id so the worker can link this wishlist
    // row to the Shopify customer instead of an anonymous cookie. The
    // value comes from window.MARAIS_CUSTOMER_ID, set in layout/theme.liquid
    // from {{ customer.id }} so only Shopify-verified logged-in sessions
    // populate it.
    var headers = { 'Accept': 'application/json' };
    var customerId = (typeof window !== 'undefined' && window.MARAIS_CUSTOMER_ID) || '';
    if (customerId) headers['X-Customer-Id'] = String(customerId);
    try {
      fetch(WL_WORKER_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: body
      }).catch(function () {});
    } catch (e) {}
  }

  function wishlistAdd(data) {
    var items = readWishlist();
    var key = String(data.productId) + ':' + String(data.variantId || '');
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].productId) + ':' + String(items[i].variantId || '') === key) {
        return { added: false };
      }
    }
    var item = {
      productId: data.productId,
      variantId: data.variantId || '',
      handle:    data.handle || '',
      title:     data.title || '',
      vendor:    data.vendor || '',
      url:       data.url || '',
      imageUrl:  data.imageUrl || '',
      price:     data.price || '',
      size:      data.size || '',
      addedAt:   Date.now()
    };
    items.push(item);
    writeWishlist(items);
    postToWorker(item);
    return { added: true };
  }
  function paintWishlistHearts() {
    var items = readWishlist();
    document.querySelectorAll('.product-card__wish').forEach(function (wish) {
      var pid = wish.getAttribute('data-product-id');
      var saved = false;
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].productId) === String(pid)) { saved = true; break; }
      }
      wish.classList.toggle('is-wished', saved);
    });
  }
  function closeAllWishPanels(exceptPanel) {
    document.querySelectorAll('.product-card__wish-panel:not([hidden])').forEach(function (p) {
      if (p === exceptPanel) return;
      p.hidden = true;
      var t = p.parentNode && p.parentNode.querySelector('[data-mh-wish-toggle]');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }
  function commitWish(wish, variantId, size) {
    var pid = wish.getAttribute('data-product-id');
    if (!pid) return;
    var result = wishlistAdd({
      productId: pid,
      variantId: variantId || '',
      handle:    wish.getAttribute('data-handle'),
      title:     wish.getAttribute('data-title'),
      vendor:    wish.getAttribute('data-vendor'),
      url:       wish.getAttribute('data-url'),
      imageUrl:  wish.getAttribute('data-image'),
      price:     wish.getAttribute('data-price'),
      size:      size || ''
    });
    wish.classList.add('is-wished');
    if (result.added) {
      showToast(size ? ('Added to wishlist — Size ' + size) : 'Added to wishlist');
    } else {
      // Already in wishlist — the "wishlist" word is a link so the
      // customer can jump straight to the saved-items page.
      if (size) {
        showToastWithLink('Size ' + size + ' is in your ', 'wishlist', window.Shopify.routes.root + 'pages/wishlist', '');
      } else {
        showToastWithLink('Already in your ', 'wishlist', window.Shopify.routes.root + 'pages/wishlist', '');
      }
    }
  }
  /* Mobile wishlist sheet ----------------------------------------
     Slides up from the bottom on mobile (≤768px) when a customer
     taps the heart on a product card. Populated from the
     originating [data-mh-wish] container's data-* attrs + cloned
     size buttons. Tracks the source card id so wishlistAdd can
     update the right card's heart-painted state after a commit. */
  var WISH_MODAL_ID = 'mh-wish-modal';
  var wishModalSourceId = null;
  function getWishModal() { return document.getElementById(WISH_MODAL_ID); }
  function setWishModalConfirmEnabled(modal, enabled) {
    if (!modal) return;
    modal.querySelectorAll('[data-mh-wish-confirm]').forEach(function (b) {
      if (enabled) {
        b.removeAttribute('disabled');
        b.setAttribute('aria-disabled', 'false');
      } else {
        b.setAttribute('disabled', '');
        b.setAttribute('aria-disabled', 'true');
      }
    });
  }
  function openWishModal(wish) {
    var modal = getWishModal();
    if (!modal || !wish) return;
    var img    = modal.querySelector('.mh-wish-modal__img');
    var brand  = modal.querySelector('.mh-wish-modal__brand');
    var title  = modal.querySelector('.mh-wish-modal__title');
    var price  = modal.querySelector('.mh-wish-modal__price');
    var sizes  = modal.querySelector('.mh-wish-modal__sizes');
    var imgSrc = wish.getAttribute('data-image') || '';
    if (img) {
      if (imgSrc) { img.src = imgSrc; img.hidden = false; }
      else { img.removeAttribute('src'); img.hidden = true; }
    }
    if (brand) brand.textContent = wish.getAttribute('data-vendor') || '';
    if (title) title.textContent = wish.getAttribute('data-title') || '';
    if (price) {
      var current = wish.getAttribute('data-price') || '';
      var was     = wish.getAttribute('data-compare-at-price') || '';
      var pct     = wish.getAttribute('data-discount-pct') || '';
      var wasEl     = price.querySelector('.mh-wish-modal__price-was');
      var currentEl = price.querySelector('.mh-wish-modal__price-current');
      var pctEl     = price.querySelector('.mh-wish-modal__price-pct');
      if (currentEl) currentEl.textContent = current;
      // On sale: show strikethrough was-price + the percentage tag.
      // Otherwise hide both extras and let the single price sit alone.
      if (wasEl) {
        if (was) { wasEl.textContent = was; wasEl.hidden = false; }
        else { wasEl.textContent = ''; wasEl.hidden = true; }
      }
      if (pctEl) {
        if (pct) { pctEl.textContent = pct + '%'; pctEl.hidden = false; }
        else { pctEl.textContent = ''; pctEl.hidden = true; }
      }
      price.classList.toggle('mh-wish-modal__price--sale', !!was);
    }
    if (sizes) {
      while (sizes.firstChild) sizes.removeChild(sizes.firstChild);
      var sourceButtons = wish.querySelectorAll('.product-card__wish-size');
      sourceButtons.forEach(function (b) {
        var clone = document.createElement('button');
        clone.type = 'button';
        clone.className = 'mh-wish-modal__size';
        clone.setAttribute('data-mh-wish-size', '');
        clone.setAttribute('data-variant-id', b.getAttribute('data-variant-id') || '');
        clone.setAttribute('data-size', b.getAttribute('data-size') || '');
        if (b.getAttribute('data-oos') === 'true') {
          clone.setAttribute('data-oos', 'true');
          clone.classList.add('is-oos');
        }
        clone.textContent = b.textContent;
        sizes.appendChild(clone);
      });
    }
    // Stash the source URL so the Notify-me path can route the
    // customer to the PDP (BIS_trigger only exists there).
    modal.setAttribute('data-mh-wish-product-url', wish.getAttribute('data-url') || '');
    // Confirm CTAs are always visible — they sit disabled until a
    // size is picked, so the customer sees the full action set
    // up-front without the modal expanding mid-flow.
    setWishModalConfirmEnabled(modal, false);
    modal.removeAttribute('data-mh-wish-pending-variant');
    modal.removeAttribute('data-mh-wish-pending-size');
    wishModalSourceId = wish.getAttribute('data-product-id') || null;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    // Force layout sync before adding `is-open` so the transform
    // transition runs from 100% → 0 rather than skipping the
    // animation entirely.
    void modal.offsetWidth;
    modal.classList.add('is-open');
    document.body.classList.add('mh-wish-modal-open');
  }
  function closeWishModal() {
    var modal = getWishModal();
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mh-wish-modal-open');
    // After the slide-down transition finishes, fully hide so the
    // backdrop doesn't keep intercepting taps.
    setTimeout(function () {
      if (!modal.classList.contains('is-open')) modal.hidden = true;
    }, 350);
    wishModalSourceId = null;
  }
  function sourceWishFromModal() {
    if (!wishModalSourceId) return null;
    return document.querySelector('[data-mh-wish][data-product-id="' + wishModalSourceId + '"]');
  }

  document.addEventListener('click', function (e) {
    // Modal close (backdrop or X button).
    if (e.target.closest('[data-mh-wish-modal-close]')) {
      e.preventDefault();
      closeWishModal();
      return;
    }
    var toggle = e.target.closest('[data-mh-wish-toggle]');
    if (toggle) {
      e.preventDefault(); e.stopPropagation();
      var wish = toggle.closest('[data-mh-wish]');
      if (!wish) return;
      var panel = wish.querySelector('.product-card__wish-panel');
      // Mobile: open the global slide-up sheet instead of the
      // inline dropdown panel (which is tight on narrow screens).
      // Single-variant products still skip the picker entirely.
      if (window.innerWidth <= 768 && panel) {
        openWishModal(wish);
        return;
      }
      if (panel) {
        var willOpen = panel.hidden;
        closeAllWishPanels(willOpen ? panel : null);
        panel.hidden = !willOpen;
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      } else {
        // No size dropdown (single-variant): add directly.
        commitWish(wish, toggle.getAttribute('data-variant-id') || '', toggle.getAttribute('data-size') || '');
      }
      return;
    }
    // Size tap — works for both the desktop inline panel and the
    // mobile slide-up sheet. Inline commits straight away (desktop's
    // one-tap pattern), sheet stores the picked size and reveals the
    // confirm pair.
    var sizeBtn = e.target.closest('[data-mh-wish-size]');
    if (sizeBtn) {
      e.preventDefault(); e.stopPropagation();
      var variantId = sizeBtn.getAttribute('data-variant-id') || '';
      var sizeVal   = sizeBtn.getAttribute('data-size') || '';
      var modal     = sizeBtn.closest('.mh-wish-modal');
      if (modal) {
        modal.querySelectorAll('[data-mh-wish-size]').forEach(function (b) {
          b.classList.toggle('is-selected', b === sizeBtn);
        });
        modal.setAttribute('data-mh-wish-pending-variant', variantId);
        modal.setAttribute('data-mh-wish-pending-size',    sizeVal);
        // OOS size → swap the right-hand CTA from "Add to bag" to
        // "Notify Me". data-mh-wish-confirm flips so the confirm
        // handler branches into the BIS path. Original ATC label is
        // cached on the button so the swap can be reversed if the
        // customer picks an in-stock size next.
        var atcBtn = modal.querySelector('[data-mh-wish-confirm="atc"], [data-mh-wish-confirm="notify"]');
        if (atcBtn) {
          if (!atcBtn.getAttribute('data-atc-label')) {
            atcBtn.setAttribute('data-atc-label', atcBtn.textContent);
          }
          if (sizeBtn.getAttribute('data-oos') === 'true') {
            atcBtn.setAttribute('data-mh-wish-confirm', 'notify');
            atcBtn.textContent = atcBtn.getAttribute('data-notify-label') || 'Notify Me';
          } else {
            atcBtn.setAttribute('data-mh-wish-confirm', 'atc');
            atcBtn.textContent = atcBtn.getAttribute('data-atc-label') || 'Add to bag';
          }
        }
        setWishModalConfirmEnabled(modal, true);
        return;
      }
      // Desktop inline panel — one-tap save.
      var wishParent = sizeBtn.closest('[data-mh-wish]');
      if (!wishParent) return;
      commitWish(wishParent, variantId, sizeVal);
      var panel2 = wishParent.querySelector('.product-card__wish-panel');
      if (panel2) panel2.hidden = true;
      var tog = wishParent.querySelector('[data-mh-wish-toggle]');
      if (tog) tog.setAttribute('aria-expanded', 'false');
      return;
    }
    // Modal confirm step — "Add to wishlist" / "Add to bag".
    var confirmBtn = e.target.closest('[data-mh-wish-confirm]');
    if (confirmBtn) {
      e.preventDefault(); e.stopPropagation();
      var modal = confirmBtn.closest('.mh-wish-modal');
      if (!modal) return;
      var sourceWish = sourceWishFromModal();
      var variantId = modal.getAttribute('data-mh-wish-pending-variant') || '';
      var sizeVal   = modal.getAttribute('data-mh-wish-pending-size') || '';
      var action    = confirmBtn.getAttribute('data-mh-wish-confirm');
      if (action === 'save') {
        if (sourceWish) commitWish(sourceWish, variantId, sizeVal);
        closeWishModal();
      } else if (action === 'notify') {
        // Cross-app reliable path: navigate to the PDP with the OOS
        // variant pre-selected, plus an mh_notify=1 flag the PDP
        // reads on load and auto-clicks its own BIS trigger from.
        // Result for the customer: one tap on the collection card →
        // PDP loads with the BIS popup already open. Works
        // regardless of which BIS app is installed (it just leans
        // on the PDP's existing notify-me path, which is already
        // wired to whatever BIS app the merchant uses).
        var productUrl = modal.getAttribute('data-mh-wish-product-url') || '';
        if (productUrl && variantId) {
          var sep = productUrl.indexOf('?') === -1 ? '?' : '&';
          location.href = productUrl + sep + 'variant=' + encodeURIComponent(variantId) + '&mh_notify=1';
        } else if (productUrl) {
          location.href = productUrl + (productUrl.indexOf('?') === -1 ? '?' : '&') + 'mh_notify=1';
        } else {
          closeWishModal();
        }
        return;
      } else if (action === 'atc') {
        if (!variantId) { closeWishModal(); return; }
        var origLabel = confirmBtn.textContent;
        confirmBtn.textContent = '…';
        confirmBtn.style.pointerEvents = 'none';
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (d.id) {
            showToast(toastString('Added', 'Added to bag'));
            if (window.MaraisCart) window.MaraisCart.refresh(true); else updateCartCount();
          } else {
            showToast(d.message || 'Could not add to bag');
          }
        })
        .catch(function () { showToast(toastString('Error', 'Could not add to bag')); })
        .finally(function () {
          confirmBtn.textContent = origLabel;
          confirmBtn.style.pointerEvents = '';
          closeWishModal();
        });
      }
      return;
    }
    // Click anywhere else closes any open desktop wishlist dropdowns.
    if (!e.target.closest('.product-card__wish-panel') && !e.target.closest('.mh-wish-modal')) {
      closeAllWishPanels(null);
    }
  });
  window.addEventListener('marais:wishlist-change', paintWishlistHearts);

  /* Size pills on product cards (rendered by product-card.liquid).
     Single click = quick add. Disabled state on sold-out sizes
     comes from the Liquid template (no `data-variant-id` attribute,
     so the closest() selector skips them). Delegated on document
     so it survives AJAX filter swaps without rebinding. */
  function initSizePills() {
    document.addEventListener('click', function (e) {
      var pill = e.target.closest('.product-card__size-pill[data-variant-id]');
      if (!pill) return;
      e.preventDefault(); e.stopPropagation();
      var variantId = pill.dataset.variantId;
      var orig = pill.textContent;
      pill.textContent = '…';
      pill.style.pointerEvents = 'none';
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d.id) { showToast(toastString('Added', 'Added to bag')); if (window.MaraisCart) { window.MaraisCart.refresh(true); } else { updateCartCount(); } }
        else showToast(d.message || 'Could not add to bag');
      })
      .catch(function () { showToast(toastString('Error', 'Could not add to bag')); })
      .finally(function () { pill.textContent = orig; pill.style.pointerEvents = ''; });
    });
  }

  /* Gender "View All" links in the category sidebar — desktop and
     tablet only. On mixed-gender collections like /collections/sale,
     the L1 "Men (View All)" / "Women (View All)" anchors render as plain
     links to /collections/men by default. Phone visitors keep that
     native nav (the sidebar is modal there — switching collection
     entirely is cleaner UX than in-place filtering inside a drawer).
     Desktop and tablet visitors get this interceptor instead: swap
     filter.p.tag=<gender> into the CURRENT URL (preserving every
     other active filter: color, size, brand, price, sort, page),
     hand the rebuilt URL to fetchFilter, AJAX swap renders the
     gendered subset in place. Bound at document level so it survives
     AJAX filter swaps without rebinding. */
  function initGenderViewAll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('.fcat-l1__link[data-gender-tag]');
      if (!link) return;
      if (window.matchMedia('(max-width: 768px)').matches) return;
      e.preventDefault();
      var tag = link.getAttribute('data-gender-tag');
      if (!tag) return;
      // Build the new URL: keep the current pathname + every existing
      // query param EXCEPT filter.p.tag (which we replace with the
      // clicked gender so the two genders are mutually exclusive —
      // clicking Women while filtered to Men switches, doesn't stack).
      var url = new URL(window.location.href);
      var params = new URLSearchParams(url.search);
      params.delete('filter.p.tag');
      params.append('filter.p.tag', tag);
      // Drop ?page= so the gender switch lands on page 1 of the new
      // result set — the old page number is meaningless once the
      // product set changes shape.
      params.delete('page');
      var next = url.pathname + '?' + params.toString();
      fetchFilter(next);
    });
  }

  /* Two-tap product cards on tablet-touch viewports (769-1100).
     Desktop reveals the size-pill hover panel on :hover. Phones
     hide it entirely and use the wish-modal sheet instead. Tablet
     sits between: no hover capability, but enough card width to
     host pills inline. We mirror the wish-modal pick-then-confirm
     flow:
       1st tap on card → arm it (.is-armed), reveal pills via the
         CSS rule scoped to the same media gate, suppress the
         anchor's PDP navigation.
       2nd tap on a pill → existing initSizePills handler commits
         the add-to-cart (this listener returns early on pills).
       2nd tap on the image (any non-pill area inside the armed
         card) → fall through, anchor navigates to PDP.
       Tap anywhere outside any card → disarm all.
     Bound at document level in CAPTURE phase so it runs before
     other delegated click handlers, and survives AJAX filter
     swaps without rebinding. */
  function initTwoTapCards() {
    var mq = window.matchMedia('(hover: none) and (pointer: coarse) and (min-width: 768px)');
    document.addEventListener('click', function (e) {
      if (!mq.matches) return;
      // Wishlist heart / wish-panel sizes / wish-modal close / size pills
      // all have their own handlers — leave them alone.
      if (e.target.closest('[data-mh-wish-toggle], [data-mh-wish-size], [data-mh-wish-modal-close], .product-card__size-pill, .product-card__wish-panel')) {
        return;
      }
      // Tap landed in the hover panel but missed a pill — suppress
      // anchor nav (the panel sits inside the .product-card__img-link).
      if (e.target.closest('.product-card__hover-panel')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      var card = e.target.closest('.product-card');
      if (!card) {
        document.querySelectorAll('.product-card.is-armed').forEach(function (c) { c.classList.remove('is-armed'); });
        return;
      }
      if (!card.classList.contains('is-armed')) {
        document.querySelectorAll('.product-card.is-armed').forEach(function (c) {
          if (c !== card) c.classList.remove('is-armed');
        });
        card.classList.add('is-armed');
        e.preventDefault();
        e.stopPropagation();
      }
      // else: armed — let the anchor navigate to PDP on this tap.
    }, true);
  }

  /* Scroll-driven second-image reveal on touch tablets (iPad).
     Desktop reveals the 2nd image + size pills on :hover; the
     (hover:none) block kills that on touch. This restores it via the
     scroll gesture: the single card the customer's finger is on when
     they start dragging flips to its 2nd image + pills (.is-img-scrolling).
     The flip is HELD after the finger lifts — the 2nd image + pills stay
     until the next action: a new scroll (which moves the flip to the card
     being scrolled on, or clears it if the scroll didn't start on a card)
     or a tap elsewhere. Pills are tappable while held (quick-add via
     initSizePills). Scoped to .collection-layout, so it covers the
     collection AND search-results grids (both reuse that wrapper);
     homepage rails sit outside it and are untouched. Passive touch
     listeners so scrolling is never blocked; document-level so it survives
     AJAX filter swaps. Skipped under reduced-motion. */
  function initScrollImageReveal() {
    var touchMq  = window.matchMedia('(hover: none) and (pointer: coarse) and (min-width: 768px)');
    var motionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    var REVEAL_CLASS = 'is-img-scrolling';
    var MOVE_THRESHOLD = 6;  // px before a touch counts as a scroll, not a tap
    var heldCard = null;     // card currently held on its 2nd image + pills
    var gestureCard = null;  // card the in-progress touch started on
    var startY = 0;
    var handled = false;     // this gesture's flip already resolved

    function revert() {
      if (heldCard) heldCard.classList.remove(REVEAL_CLASS);
      heldCard = null;
    }

    document.addEventListener('touchstart', function (e) {
      handled = false;
      gestureCard = null;
      if (!touchMq.matches || motionMq.matches) return;
      var t = e.touches && e.touches[0];
      if (!t || !e.target || !e.target.closest) return;
      gestureCard = e.target.closest('.collection-layout .product-card');
      startY = t.clientY;
    }, { passive: true });

    document.addEventListener('touchmove', function (e) {
      // A scroll gesture is the "next action" that moves the held flip.
      if (handled || !touchMq.matches || motionMq.matches) return;
      var t = e.touches && e.touches[0];
      if (!t || Math.abs(t.clientY - startY) < MOVE_THRESHOLD) return;
      handled = true;
      // The previously held card reverts as soon as a new scroll begins on
      // a different card (or on no card at all).
      if (heldCard && heldCard !== gestureCard) {
        heldCard.classList.remove(REVEAL_CLASS);
        heldCard = null;
      }
      if (gestureCard) {
        gestureCard.classList.add(REVEAL_CLASS);
        heldCard = gestureCard;
      }
    }, { passive: true });

    // touchend/touchcancel intentionally do NOT revert: the flip is held
    // after the finger lifts so the 2nd image + pills stay put.

    // A tap elsewhere is the other "next action" that dismisses the held
    // flip. A scroll never fires a click, so this only catches deliberate
    // taps. Taps inside the held card (a size pill -> quick-add, the image
    // -> PDP) are "on the card", not elsewhere, so they leave it held. The
    // onHeld geometry check below is what protects those on-card taps — this
    // runs in capture phase, so it fires regardless of any stopPropagation
    // from initSizePills / initTwoTapCards. Don't remove the check.
    document.addEventListener('click', function (e) {
      if (!heldCard) return;
      var onHeld = e.target && e.target.closest &&
                   e.target.closest('.collection-layout .product-card') === heldCard;
      if (!onHeld) revert();
    }, true);
  }

  /* ────────────────────────────────────────────────────────────
     INIT — re-runs after every AJAX swap
     ────────────────────────────────────────────────────────────
     fetchFilter() calls this to rebind handlers on the freshly
     swapped DOM. initSizePills + initTwoTapCards live outside
     (document-level delegation, only need binding once). */
  function initFiltersAndUI() {
    initFilterCheckboxes();
    initSortSelect();
    initGridToggle();
    initMobileSidebar();
    initPriceForm();
    initColourSwatches();
    paintWishlistHearts();
    initColourLabel();
    initQuickAdd();
    updateClearButton();
    // Notify other scripts (e.g. the alt-image toggle that lives in
    // the section's inline <script>) so they can re-sync their state
    // on the freshly-swapped DOM. Re-init at the end so listeners
    // see the rebound handlers already in place.
    document.dispatchEvent(new CustomEvent('marais:filters-reinit'));
  }

  /* Exposes the actual sticky-header height to CSS as --mh-nav-h
     so collection content can offset properly. ResizeObserver
     fires after layout is fully committed on ANY header height
     change — viewport resize, font swap, mega-menu open, content
     reflow — so --mh-nav-h can't go stale the way the old
     window.resize-only hook did. The window.resize fallback is for
     browsers without ResizeObserver (pre-2020 Safari only). */
  function setNavHeight() {
    var h = document.getElementById('mh-header');
    if (h) document.documentElement.style.setProperty('--mh-nav-h', h.offsetHeight + 'px');
  }

  document.addEventListener('DOMContentLoaded', function () {
    setNavHeight();
    var hdr = document.getElementById('mh-header');
    if (hdr && typeof ResizeObserver === 'function') {
      new ResizeObserver(setNavHeight).observe(hdr);
    } else {
      window.addEventListener('resize', setNavHeight);
    }
    initFiltersAndUI();
    initSizePills();
    initTwoTapCards();
    initScrollImageReveal();
    initGenderViewAll();
    document.querySelectorAll('.js-coll-read-more').forEach(function (btn) {
      btn.addEventListener('click', function () {
        btn.closest('.js-coll-desc').classList.add('is-open');
      });
    });
  });

})();
